import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Check } from "lucide-react";
import { toast } from "sonner";

export default function Tasks() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", due_at: "", priority: "MEDIUM", notes: "" });
  const load = async () => { const { data } = await api.get("/sales/tasks"); setItems(data); };
  useEffect(() => { load(); }, []);
  const save = async () => { try { await api.post("/sales/tasks", form); toast.success("Task added"); setOpen(false); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const done = async (t) => { await api.post(`/sales/tasks/${t.id}/done`); load(); };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="sales-tasks">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Daily tasks</h1>
          <p className="text-muted-foreground">Your personal task list — follow-ups, meetings, reminders.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="task-create-btn"><Plus className="w-4 h-4 mr-1" /> New task</Button>
      </div>
      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Due</TableHead><TableHead>Priority</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((t) => (
              <TableRow key={t.id} data-testid={`task-row-${t.id}`} className={t.done ? "opacity-60 line-through" : ""}>
                <TableCell className="font-semibold">{t.title}<div className="text-xs text-muted-foreground">{t.notes}</div></TableCell>
                <TableCell className="font-mono text-xs">{t.due_at ? new Date(t.due_at).toLocaleString() : "—"}</TableCell>
                <TableCell><Badge variant="outline">{t.priority}</Badge></TableCell>
                <TableCell>{t.done ? <Badge>Done</Badge> : <Badge variant="outline">Open</Badge>}</TableCell>
                <TableCell className="text-right">{!t.done && <Button size="sm" onClick={() => done(t)} data-testid={`task-done-${t.id}`}><Check className="w-4 h-4 mr-1" />Done</Button>}</TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No tasks yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" /></div>
            <div><Label>Due at</Label><Input type="datetime-local" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} /></div>
            <div><Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["LOW","MEDIUM","HIGH"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="task-save">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
